import { NetworkStatus, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { ShareAssetStatusEnumType } from '__generated__/graphql';
import { FC } from 'react';

import LoadingIndicator from 'primitives/LoadingIndicator';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterPanel from 'components/FilterPanel';

import statusEnumToReadable from 'utils/enums/status-enum-to-readable';

const SHARE_ASSETS_APPROVAL_QUERY = gql(`
  query fetchShareAssetsForApproval(
    $cursor: ID
    $limit: Int
    $filters: ShareAssetsFilterType
  ) {
    shareAssets(cursor: $cursor, limit: $limit, filters: $filters) {
      nodes {
        id
        numberOfShares
        status
        createdAt
        deal {
          id
          schemeName
          companyInformation {
            id
            company {
              id
              name
            }
          }
        }
        hasIssueWithValues
      }
      statistics {
        shareAssetStatisticsBreakdownByShareAssetStatus {
          status
          numberOfShareAssets
        }
      }
      pageInfo {
        hasNextPage
        cursor
      }
    }
  }
`);

const ShareAssetsPage: FC = () => {
  const { loading, error, data, refetch, fetchMore, networkStatus, variables } = useQuery(
    SHARE_ASSETS_APPROVAL_QUERY,
    {
      variables: {
        limit: 50,
        filters: {
          status: ShareAssetStatusEnumType.UnderReview,
        },
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const shareAssets = data.shareAssets.nodes;
  const pageInfo = data.shareAssets.pageInfo;
  const statistics = data.shareAssets.statistics;

  const filters = [
    {
      type: 'status',
      label: 'Status',
      options: [
        {
          label: 'All',
          value: undefined,
        },
        ...statistics.shareAssetStatisticsBreakdownByShareAssetStatus.map(
          ({ status, numberOfShareAssets }) => ({
            label: `${statusEnumToReadable(status).label} (${numberOfShareAssets})`,
            value: status,
          })
        ),
      ],
      readable: statusEnumToReadable,
    },
  ];

  return (
    <>
      <FilterPanel activeFilters={variables?.filters} filters={filters} refetch={refetch} />
      <DataTable
        data={shareAssets}
        onClick={shareAsset =>
          window.open(
            `/companies/${shareAsset.deal.companyInformation.company.id}/assets`,
            '_blank'
          )
        }
        columns={[
          {
            label: 'Deal',
            fieldName: 'deal.schemeName',
          },
          {
            label: 'Status',
            fieldName: 'status',
            type: 'STATUS',
          },
          {
            label: 'Number of Shares',
            fieldName: 'numberOfShares',
          },
          {
            label: 'Has Issue With Values',
            fieldName: 'hasIssueWithValues',
            type: 'BOOLEAN',
          },
          {
            label: 'Created At',
            fieldName: 'createdAt',
            type: 'DATE',
          },
        ]}
        hasNextPage={pageInfo.hasNextPage}
        filterLoading={networkStatus === NetworkStatus.setVariables}
        paginationLoading={loading}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
      />
    </>
  );
};

export default ShareAssetsPage;
